import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Col, FormContainer, Icon, Row } from '@wyz/ui';
import { useForm } from 'react-hook-form';
import { SelectField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { ContainerSearchHelpers } from '~/purchase/helpers';
import { searchTyreFilterSchema } from '~/purchase/schemas';
import {
  IContainerSearchResult,
  IContainerSearchTyreFilter,
} from '~/purchase/types';

type Props = {
  onSubmit: (formData: IContainerSearchTyreFilter | undefined) => void;
  offers: IContainerSearchResult['offers'];
};
const ContainerSearchFilterBar = ({ offers, onSubmit }: Props) => {
  const { t } = useI18n();

  const fieldMethods = useForm({
    resolver: zodResolver(searchTyreFilterSchema),
    defaultValues: {
      identifier: undefined,
      brand: undefined,
      season: undefined,
    },
  });

  const { brandsLookup, seasonsLookup, eanLookup, dimensionLookup } =
    ContainerSearchHelpers.buildLookupTables(offers);

  return (
    <FormContainer
      methods={fieldMethods}
      submitHandler={(formData) => {
        onSubmit(formData);
      }}
    >
      <Row>
        <Col md={3} className='d-flex align-items-center space-x-2'>
          <Icon icon='tune' className='mb-2' />
          <div className='flex-1'>
            <SelectField
              name='identifier'
              options={[
                {
                  label: t('app.common.product.ean'),
                  options: Object.values(eanLookup).map((ean) => ({
                    label: ean,
                    value: ean,
                  })),
                },
                {
                  label: t('app.common.product.dimension'),
                  options: Object.values(dimensionLookup).map((dimension) => ({
                    label: dimension,
                    value: dimension,
                  })),
                },
              ]}
              placeholder={'DIMENSION / EAN'}
              label={null}
              size='sm'
            />
          </div>
        </Col>
        <Col md={2}>
          <SelectField
            name='brand'
            options={Object.values(brandsLookup).map((brand) => ({
              label: brand.name,
              value: brand.id,
            }))}
            placeholder={<Trans i18nKey='app.common.product.brand' />}
            isDisabled={Object.keys(brandsLookup).length === 0}
            size='sm'
            isClearable
          />
        </Col>
        <Col md={2}>
          <SelectField
            name='season'
            options={Object.values(seasonsLookup).map((season) => ({
              label: t(season.i18nKey),
              value: season.id,
            }))}
            isLoading={Object.keys(seasonsLookup).length === 0}
            placeholder={<Trans i18nKey='app.common.product.season' />}
            size='sm'
            isClearable
          />
        </Col>
        <Col md={3} className='d-flex'>
          <Button id='submit-filter' variant='primary' type='submit' size='sm'>
            <Trans i18nKey='app.common.buttons.apply' />
          </Button>
          <Button
            id='reset-filter'
            type='reset'
            variant='link'
            size='sm'
            onClick={() => {
              fieldMethods.reset();
              onSubmit(undefined);
            }}
          >
            <Trans i18nKey='app.common.buttons.reset' />
          </Button>
        </Col>
      </Row>
    </FormContainer>
  );
};

export default ContainerSearchFilterBar;
